import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';

/**
 * Classe astratta che consente di memorizzare sul DB, in un array, una o più
 * entità generiche.
 *
 * NON va usata come DB ma per interagire con pochi elementi come preferenze
 * utente o strutture dati particolari.
 *
 * Le classi che la estendono dovranno implementare metodi pubblici per interagire
 * con i propri dati di dominio
 */
@Injectable()
export abstract class MemorizzatoreEntitaService<T> {
  protected elencoEntita: T[];

  constructor(private storage: Storage) { }
  /**
   * Metodo da definire nelle classi finire per ottenere la chiave con cui
   * accedere al DB
   */
  protected abstract getChiaveDB(): string;
  /**
   * Ritorna true quando i due oggetti
   * @param a
   * @param b
   */
  protected abstract verificaUguaglianzaEntita(a: T, b: T): boolean;

  // IMPLEMENTAZIONI COMUNI
  /**
   * Memorizza nello stato del provider
   * @param tipoEntita fornisce la chiave del DB da cui ottenere le entità
   */
  protected async caricaEntitaSalvate(): Promise<T[]> {
    const entitaSalvate: T[] = await this.getElencoEntita();
    this.elencoEntita = entitaSalvate; // Effetto collaterale
    return entitaSalvate;
  }

  /**
   * Ritorna l'elenco delle entità memorizzate sul DB di un certo tipo.
   * Viene SEMPRE ritornato un array, al massimo vuoto se non vi era mai stato salvato
   * nulla prima
   */
  protected async getElencoEntita(): Promise<T[]> {
    let entitaOffline: T[] = [];
    try {
      const json =  await this.storage.get(this.getChiaveDB());
      // entitaOffline = JSON.parse(json.value);
      if (typeof json === 'string') entitaOffline = JSON.parse(json);
    } catch (error) {
      this.mostraErrore(error);
    }
    return (entitaOffline && entitaOffline.length
      && entitaOffline.length > 0) ? entitaOffline : []; // Ritorno sempre un array, al massimo vuoto
  }

  /**
   * Salva l'entita o l'array di entita passate sul DB rimuovendo 
   * prima dallo storico ogni istanza t.c. verificaEntita(entita, storica): true
   * e poi l'input viene appeso e salvato; cioè: 
   * - per le entità rimuove dall'array che è lo storico su Db tutte le istanze
   * ad essa uguali. Poi l'entita' viene aggiunta in coda e l'array salvato
   * - per ogni entità dell'array dato, ciascuna viene prima rimossa, se presente,
   * dallo storico. Poi l'array viene concatenato in coda e il risultato salvato
   * @param entita oggetto o array di oggetti da salvare sovrascrivendo
   * le istanze "compatibili" @see verificaUguaglianzaEntita()
   */
  protected async aggiornaEntita(entita: T | T[]) {
    try {
      if (entita instanceof Array) {
        let entitaSalvatePrecedentemente = await this.getElencoEntita();
        // Rimuovo le occorrenze compatibili con l'input
        for (const nuovaEntita of entita) {
          entitaSalvatePrecedentemente = entitaSalvatePrecedentemente.filter(entitaStorica => {
            return !(this.verificaUguaglianzaEntita(nuovaEntita, entitaStorica));
          });
        }
        // Aggiungo all'elenco filtrato l'input originale
        entitaSalvatePrecedentemente.concat(entita);
        await this.storage.set(this.getChiaveDB(),JSON.stringify(entitaSalvatePrecedentemente));
      } else {
        let entitaSalvatePrecedentemente = await this.getElencoEntita();
        // Rimuovo le occorrenze compatibili con l'input
        entitaSalvatePrecedentemente = entitaSalvatePrecedentemente.filter(entitaStorica => {
          return !(this.verificaUguaglianzaEntita(entita, entitaStorica));
        });
        // Aggiungo all'elenco filtrato l'input originale
        entitaSalvatePrecedentemente.push(entita);
        await this.storage.set(this.getChiaveDB(),JSON.stringify(entitaSalvatePrecedentemente));
      }

      await this.caricaEntitaSalvate(); // Ri-attualizzo i dati del servizio
      return true;
    } catch (error) {
      this.mostraErrore(error);
      return false;
    }
  }

  /**
   * Salva l'entità o l'array di entità passate sul DB
   * @param entita oggetto o array di oggetti da salvare
   * @param sovrascrivi TRUE forza la sovrascrittura di TUTTO quanto era presente
   * nello storico ALTRIMENTI il dato viene concatenato in coda. Di default è false
   */
  protected async salvaEntita(entita: T | T[], sovrascrivi: boolean = false): Promise<boolean> {
    try {
      const entitaSalvatePrecedentemente = await this.getElencoEntita();

      if (entita instanceof Array) {
        await this.memorizzaArray(entita, entitaSalvatePrecedentemente, sovrascrivi);
      } else {
        await this.memorizzaEntitaSingola(entita, entitaSalvatePrecedentemente, sovrascrivi);
      }

      await this.caricaEntitaSalvate(); // Ri-attualizzo i dati del servizio
      return true;
    } catch (error) {
      this.mostraErrore(error);
      return false;
    }
  }

  private async memorizzaArray(arrayDaSalvare: T[], vecchioArray: T[], sovrascrivi: boolean): Promise<void> {
    if ((sovrascrivi) || (vecchioArray == null) || (vecchioArray.length === 0)) { // Scrivo l'array
      await this.storage.set(this.getChiaveDB(),JSON.stringify(arrayDaSalvare));
    } else { // aggiungo al contenuto già presente
      const newArray = vecchioArray.concat(arrayDaSalvare);
      await this.storage.set(this.getChiaveDB(),JSON.stringify(newArray));
    }
  }

  private async memorizzaEntitaSingola(entita: T, vecchioArray: T[], sovrascrivi: boolean): Promise<void> {
    if ((sovrascrivi) || (vecchioArray == null) || (vecchioArray.length === 0)) { // Memorizzo l'array
      await this.storage.set(this.getChiaveDB(),JSON.stringify([entita]));
    } else { // aggiungo al contenuto già presente
      vecchioArray.push(entita);
      await this.storage.set(this.getChiaveDB(),JSON.stringify(vecchioArray));
    }
  }

  /**
   * Cancella l'entità fornita dall'elenco memorizzato offline.
   *
   * Se non viene fornito alcun parametro viene rimosso interamente
   * il contenuto su Storage
   * @param entitaDaRimuovere opzionale oggetto da rimuovere
   * (paragonando tutti gli oggetti dati e tutti quelli presenti su DB tramite
   * verificaUguaglianza)
   */
  protected async rimuoviEntita(entitaDaRimuovere?: T) {
    try {
      if (entitaDaRimuovere) {
        await this.rimuoviSingolaEntita(entitaDaRimuovere);
      } else {
        await this.rimuoviElencoEntita();
      }
      await this.caricaEntitaSalvate(); // Ri-attualizzo i dati del servizio
      return true;
    } catch (error) {
      this.mostraErrore(error);
      return false;
    }
  }

  /**
   * Rimuove dal DB l'elenco delle entità precedentemente memorizzate
   * @param tipoEntita
   */
  protected async rimuoviElencoEntita(): Promise<void> {
    await this.storage.remove(this.getChiaveDB());
  }

  /**
   * Tentativo di cancellare dall'elenco delle entità memorizzato sul DB l'oggetto fornito. Se non viene
   * trovato viene ri-salvato l'elenco precedente
   * @param entitaDaRimuovere
   */
  private async rimuoviSingolaEntita(entitaDaRimuovere: T): Promise<boolean> {
    let entitaSalvatePrecedentemente = await this.getElencoEntita();
    entitaSalvatePrecedentemente = entitaSalvatePrecedentemente.filter(entitaSuStorico => {
      return !this.verificaUguaglianzaEntita(entitaSuStorico, entitaDaRimuovere);
    });
    const salvataggioCompletato = await this.salvaEntita(entitaSalvatePrecedentemente, true);
    return salvataggioCompletato;
  }

  // TODO:, gestire gravità e logging
  protected mostraErrore(error?) {
    console.error('Errore in fase di lettura dell\'entità', error);
  }
}
